import {finalize} from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { find } from 'lodash';

import { SLEntity, SLAddress, SLEntityField } from './entity';
import { SLEntityService } from './entity.service';
import { NotifierService, DialogService } from '../../ui';
import { DossierModel } from '../../dossier-model/dossier-model';
import { RecordSearchDialogComponent } from '../../record/record-search-dialog.component';
import { RecordService } from '../../record/record.service';
import { Record } from '../../record/record';

type ViewMode = 'view' | 'new' | 'edit';

@Component({
  templateUrl: './entity-edit.component.html',
})
export class SLEntityEditComponent implements OnInit {

  entity: SLEntity
  dossierModel: DossierModel

  mode: ViewMode
  title: string

  saving: boolean = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private entityService: SLEntityService,
    private recordService: RecordService,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) { }

  ngOnInit() {

    this.mode = this.route.routeConfig.data.mode;
    this.title = this.route.routeConfig.data.title;

    switch (this.mode) {
      case 'new': 
        {
          // Get the dossier model
          this.notifier.startProgress();

          this.route.data.subscribe((data) => {
            this.dossierModel = data.dossierModel;

            this.entity = new SLEntity({
              type: 'COMPANY',
              address: {
                line1: '',
                line2: '',
                line3: '',
                city: '',
                zip: '',
                country: 'France',
              },
              extraFields: [],
              movements: [],
            });

            this.notifier.endProgress();
          });
        }
        break;

      case 'edit':
        {
          // Get the entity
          this.notifier.startProgress();

          this.route.data.subscribe((data) => {
            this.entity = data.entity;
            this.dossierModel = data.dossierModel;
            
            this.notifier.endProgress();
          });
        }
        break;
        
        case 'view':
          {
            // Get the entity
            this.notifier.startProgress();
            
            this.route.data.subscribe((data) => {
              this.entity = data.entity;
              this.dossierModel = data.dossierModel;

            this.notifier.endProgress();
          });
        }
        break;
    }
  }

  save() {
    if (this.saving || !this.entity || !this.entity.valid) {
      return;
    }

    this.saving = true;

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    switch (this.mode) {
      case 'new':
        {
          this.entityService
            .create(this.entity).pipe(
            finalize(onComplete))
            .subscribe((entity) => {
              this.notifier.success('Entité créée avec succès');

              // Got to view screen
              this.router.navigate(['shares-ledger', 'entities', entity.id]);
            }, () => {
              this.saving = false;
            });
        }
        break;

      case 'edit':
        {
          this.entityService
            .update(this.entity.id, this.entity).pipe(
            finalize(onComplete))
            .subscribe((entity) => {
              this.notifier.success('Entité enregistrée avec succès');

              // Got to view screen
              this.router.navigate(['shares-ledger', 'entities', entity.id]);
            }, () => {
              this.saving = false;
            });
        }
        break;
    }
  }

  cancel() {
    if (this.mode === 'edit') {
      this.router.navigate(['shares-ledger', 'entities', this.entity.id]);
    }
    else {
      this.router.navigate(['shares-ledger', 'entities']);
    }
  }

  delete() {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer cette entité?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.entityService
          .delete(this.entity.id)
          .subscribe((id) => {
            this.notifier.success('Entité supprimée avec succès');

            this.router.navigate(['shares-ledger', 'entities']);

          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }

  importRecord() {
    this.dialogService.show({
      component: RecordSearchDialogComponent,
      props: {

        onClose: () => {
          this.dialogService.hide();
        },

        onSelect: (record: Record) => {
          this.dialogService.hide();

          this.notifier.startProgress();

          this.recordService
            .record(record.id).pipe(
            finalize(() => {
              this.notifier.endProgress();
            }))
            .subscribe((record) => {

              // Name
              this.entity.name = record.name;

              // RCS
              const rcsField = find(record.fields, f => f.name === process.env.SL_RCS_FIELD_NAME);

              if (rcsField) {
                this.entity.rcs = rcsField.value;
              }

              // Email
              const emailField = find(record.fields, f => f.name === process.env.SL_EMAIL_FIELD_NAME);

              if (emailField) {
                this.entity.email = emailField.value;
              }

              // Phone
              const phoneField = find(record.fields, f => f.name === process.env.SL_PHONE_FIELD_NAME);

              if (phoneField) {
                this.entity.phone = phoneField.value;
              }

              // Address
              const addressField = find(record.fields, f => f.name === process.env.SL_ADDRESS_FIELD_NAME);
              
              if (addressField) {
                this.entity.address.line1 = addressField.value;
              }

              // Other fields
              this.entity.extraFields = record.fields.map(f => new SLEntityField({
                name: f.name,
                value: f.value,
              }));

              // Re-create the entity object
              this.entity = new SLEntity(this.entity);
            });
        }
      }
    });

    event.preventDefault();
  }
}
